/* eslint-disable max-len */
import { Dispatch, useLayoutEffect, useRef, useState } from "react";
import { IconButton, Avatar } from "@mui/material";
import { useQuery } from "@apollo/client";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as User from "providers/user";
import { GET_ARTISTS } from "../gql";

interface CalendarArtistsFilterProps {
  artistFilter: string;
  setArtistFilter: Dispatch<React.SetStateAction<string>>;
  permissions: {
    isSuperAdmin: boolean;
    isOwner: boolean;
    isManager: boolean;
    isArtist: boolean;
    role: { id: string; role: string; company_id: string; user_id: string };
    companyId: string | undefined;
  };
}

const CalendarArtistsFilter = ({
  artistFilter,
  setArtistFilter,
  permissions
}: CalendarArtistsFilterProps) => {
  const { user } = User.useUser();
  const artistsResult = useQuery(GET_ARTISTS, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: { equals: permissions?.companyId },
            ...(permissions?.isArtist && {
              id: { equals: permissions?.role?.user_id }
            })
          }
        },
        is_active: {
          equals: true
        }
      }
    },
    fetchPolicy: "no-cache"
  });
  const data = artistsResult?.data ?? artistsResult?.previousData;
  const ref = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState<boolean>(false);

  useLayoutEffect(() => {
    const checkScroll = () => {
      if (ref?.current && ref.current.scrollWidth > ref.current.clientWidth) {
        setShowScrollButtons(true);
      } else {
        setShowScrollButtons(false);
      }
    };

    const setup = () => {
      if (ref.current) {
        checkScroll(); // initial check immediately

        if ("ResizeObserver" in window) {
          const resizeObserver = new ResizeObserver(checkScroll);
          resizeObserver.observe(ref.current);

          return () => resizeObserver.disconnect();
        }
      }
    };

    const cleanUp = setup();
    return cleanUp;
  }, [data]);

  const scrollLeft = () => {
    ref?.current?.scrollBy({
      left: -500,
      behavior: "smooth"
    });
  };

  const scrollRight = () => {
    ref?.current?.scrollBy({
      left: 500,
      behavior: "smooth"
    });
  };

  return (
    <div
      className={`w-full flex justify-between items-center h-[54px] absolute ${
        user.impersonate_company_id ? "top-[94px]" : "top-[54px]"
      } left-0 ${
        window.location.pathname === "/dashboard/Services-calendar" ||
        window.location.pathname === "/dashboard"
          ? "right-[16px]"
          : ""
      } bg-[#F6F4F1] border-t border-[#ddd]`}
    >
      {showScrollButtons && (
        <IconButton className="h-[40px] w-[40px]" onClick={scrollLeft}>
          <ChevronLeftIcon />
        </IconButton>
      )}
      <div ref={ref} className="w-full hide-scroll-bar overflow-x-auto">
        <div
          className={`flex w-full h-[54px] ${
            showScrollButtons ? "justify-between" : "justify-center"
          } items-center gap-[24px]`}
        >
          {data?.artists?.map((item: any) => (
            <div
              key={item?.user?.id}
              onClick={() =>
                setArtistFilter((prev: any) => {
                  if (permissions?.isArtist) return permissions?.role?.user_id;
                  return prev === item?.user?.id ? "" : item?.user?.id;
                })
              }
              aria-hidden
              className={`flex just items-center gap-[8px] pr-[10px] rounded-[20px] cursor-pointer transition-colors duration-200 ease-in-out ${
                item?.user?.id !== artistFilter ? "hover:!bg-mono-50" : ""
              }`}
              style={{
                backgroundColor:
                  item?.user?.id === artistFilter
                    ? "rgba(184, 114, 42, 0.1)"
                    : "transparent"
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "rgba(238, 234, 228, 0.9)",
                  color: "rgba(48, 47, 46, 0.95)",
                  fontFamily: "Inter",
                  fontSize: "18px"
                }}
                alt={item?.user?.first_name || "avatar"}
                src={
                  item?.cover_photo_url || item?.user?.photo_url || "monogram"
                }
              />
              <div
                className="max-w-[150px] truncate whitespace-nowrap text-[16px] font-[400] font-['Inter']"
                style={{
                  color:
                    item?.user?.id === artistFilter
                      ? "#B8722A"
                      : "rgba(48, 47, 46, 0.95)"
                }}
              >
                {item?.user?.first_name + " " + item?.user?.last_name}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showScrollButtons && (
        <IconButton className="h-[40px] w-[40px]" onClick={scrollRight}>
          <ChevronRightIcon />
        </IconButton>
      )}
    </div>
  );
};

export default CalendarArtistsFilter;
